import { z } from 'zod';
import { dateStringSchema, emailSchema, nameSchema, phoneSchema } from './common.js';

const programModeSchema = z.enum(['online', 'offline', 'hybrid']);
const userStatusSchema = z.enum(['active', 'inactive', 'archived']);
const genderSchema = z.enum(['male', 'female', 'unspecified']);

/**
 * Referral code captured during student creation (or at admissions). Optional
 * because most enrolments are organic. Format `TCH-BBL-XXXXXX` or
 * `STU-BBL-XXXXXX`; validated by the wallet validation contract on the
 * server. We keep it loose here (just a length-bounded string) so unknown
 * future prefixes don't block enrolment — the attribution code path itself
 * does the strict format check.
 */
const optionalReferralCodeSchema = z
  .string()
  .trim()
  .min(8, 'Referral code is too short')
  .max(40, 'Referral code is too long')
  .optional();

const referralSourceSchema = z
  .enum(['admissions_form', 'admin_enrollment', 'activation'])
  .optional();

export const studentCreateSchema = z
  .object({
    admissionNumber: z.string().trim().min(3).max(30),
    name: nameSchema,
    dateOfBirth: dateStringSchema,
    gender: genderSchema,
    guardianName: nameSchema,
    guardianRelation: z.string().trim().min(2).max(40),
    address: z.string().trim().min(5).max(500),
    contact: phoneSchema,
    email: emailSchema.optional(),
    batchId: z.string().uuid().optional(),
    programMode: programModeSchema,
    feeDueDay: z.number().int().min(1).max(28).default(5),
    householdId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    referralCode: optionalReferralCodeSchema,
    referralSource: referralSourceSchema,
    admissionEnquiryId: z.string().uuid().optional(),
  })
  .strict();

export const studentUpdateSchema = studentCreateSchema.partial().extend({
  status: userStatusSchema.optional(),
});

export const teacherCreateSchema = z
  .object({
    employeeId: z.string().trim().min(2).max(30),
    name: nameSchema,
    dateOfBirth: dateStringSchema,
    gender: genderSchema,
    email: emailSchema,
    contact: phoneSchema,
    address: z.string().trim().min(5).max(500),
    qualifications: z.array(z.string().min(2).max(120)).max(20).default([]),
    languages: z.array(z.string().min(2).max(40)).max(10).default([]),
    canTeachPrograms: z.array(z.string().min(2).max(80)).max(20).default([]),
    bio: z.string().max(2000).optional(),
  })
  .strict();

export const teacherUpdateSchema = teacherCreateSchema.partial().extend({
  status: userStatusSchema.optional(),
});

export const managerCreateSchema = teacherCreateSchema.extend({
  scope: z.string().max(200).optional(),
});

export const managerUpdateSchema = managerCreateSchema.partial().extend({
  status: userStatusSchema.optional(),
});

export const householdCreateSchema = z
  .object({
    primaryContactName: nameSchema,
    primaryPhone: phoneSchema,
    primaryEmail: emailSchema,
    address: z.string().trim().min(5).max(500),
    students: z.array(z.string().uuid()).min(1, 'At least one student must be linked'),
  })
  .strict();

export const householdUpdateSchema = householdCreateSchema.partial();

export const householdMemberCreateSchema = z
  .object({
    householdId: z.string().uuid(),
    name: nameSchema,
    relation: z.string().trim().min(2).max(40),
    phone: phoneSchema,
    email: emailSchema.optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

export const householdMemberUpdateSchema = householdMemberCreateSchema
  .partial()
  .omit({ householdId: true });

export const householdLinkStudentsSchema = z
  .object({
    studentIds: z.array(z.string().uuid()).min(1, 'At least one student id is required'),
    primaryStudentId: z.string().uuid().optional(),
  })
  .strict();

export const parentMessageCreateSchema = z
  .object({
    studentId: z.string().uuid(),
    subject: z.string().trim().min(2).max(120),
    body: z.string().trim().min(2).max(4000),
  })
  .strict();

export type StudentCreateInput = z.infer<typeof studentCreateSchema>;
export type StudentUpdateInput = z.infer<typeof studentUpdateSchema>;
export type TeacherCreateInput = z.infer<typeof teacherCreateSchema>;
export type TeacherUpdateInput = z.infer<typeof teacherUpdateSchema>;
export type ManagerCreateInput = z.infer<typeof managerCreateSchema>;
export type ManagerUpdateInput = z.infer<typeof managerUpdateSchema>;
export type HouseholdCreateInput = z.infer<typeof householdCreateSchema>;
export type HouseholdUpdateInput = z.infer<typeof householdUpdateSchema>;
export type HouseholdMemberCreateInput = z.infer<typeof householdMemberCreateSchema>;
export type HouseholdMemberUpdateInput = z.infer<typeof householdMemberUpdateSchema>;
export type HouseholdLinkStudentsInput = z.infer<typeof householdLinkStudentsSchema>;
export type ParentMessageCreateInput = z.infer<typeof parentMessageCreateSchema>;
